import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  OnDestroy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
} from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../core/services/auth.service';
import { LoaderService } from '../../core/services/loader.service';
import { CommonService } from '../../shared/services/common.service';
import {
  OrganizationModel,
  userDetailModel,
} from '../../features/organizations/organization.interface';
import { OrganizationSelectionService } from '../../core/services/organization-selection.service';
import { OrganizationService } from '../../features/organizations/organization.service';

export interface OrganizationOption {
  id: number;
  value: string;
  organizationName?: string;
  parentOrganizationId?: number | null;
}

@Component({
  selector: 'app-organization-search',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzSelectModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormControlComponent,
  ],
  templateUrl: './organization-search.component.html',
  styleUrls: ['./organization-search.component.scss'],
})
export class OrganizationSearchComponent implements OnInit, OnDestroy {
  @Input() defaultSelectedOrg: number | null = null;
  @Input() label = 'School Search (Clear All Before Searching)';
  @Input() placeholder = 'Select a school';
  @Input() isNullable = true;
  @Input() disabled = false;
  @Input() allowedClear = false;
  @Output() orgSearch = new EventEmitter<string>();
  @Output() orgChange = new EventEmitter<number | null>();

  public orgList: OrganizationOption[] = [];
  userData: userDetailModel | null = null;
  private readonly destroy$ = new Subject<void>();
  readonly loaderService = inject(LoaderService);
  readonly commonService = inject(CommonService);
  readonly authService = inject(AuthService);
  readonly orgSelectionService = inject(OrganizationSelectionService);
  readonly orgService = inject(OrganizationService);
  ngOnInit() {
    this.authService.userData$.pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.userData = res;
    });

    this.orgSelectionService.orgId$
      .pipe(takeUntil(this.destroy$))
      .subscribe(orgId => {
        if (orgId === null) {
          this.defaultSelectedOrg = this.isNullable ? 0 : null;
        } else if (orgId !== this.defaultSelectedOrg) {
          this.defaultSelectedOrg = orgId;
        }
      });

    this.commonService.orgList$
      .pipe(takeUntil(this.destroy$))
      .subscribe(orgs => {
        this.orgList = orgs;
        if (!this.isNullable) {
          this.defaultSelectedOrg = orgs.length ? orgs[0].id : null;
          this.onChange(this.defaultSelectedOrg);
        }
      });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(value: string) {
    this.orgSearch.emit(value);
    if (this.userData?.isSuperAdmin) {
      this.commonService
        .getDataByType('Organization', value)
        .pipe(takeUntil(this.destroy$))
        .subscribe(orgList => {
          this.orgList = orgList.result;
        });
    } else {
      this.orgService
        .getOrganizationsToSwitch()
        .pipe(takeUntil(this.destroy$))
        .subscribe(orgList => {
          if (orgList?.result) {
            const mappedOrgs = orgList.result.map(
              (item: OrganizationModel) => ({
                id: item.id ?? 0,
                value: item.organizationName ?? '',
              })
            );

            this.orgList = value
              ? mappedOrgs.filter(org =>
                  org.value.toLowerCase().includes(value.toLowerCase())
                )
              : mappedOrgs;
          }
        });
    }
  }

  onChange(value: number | null) {
    this.orgChange.emit(value);
    this.orgSelectionService.setOrgId(value);
  }
}
